import { useConfigurator } from '@/app/store/configurator/hooks/use-configurator';
import i18n from 'i18next';
import { getLevelsLabel } from '../lib/configurator';
import { SectionParam } from '../types/section';
import { MAX_SHELF_WEIGHT, RACK_MATERIALS_OPTIONS, SHELF_DECK, SHELF_HEIGHT } from './configurator';

const WALL_MOUNTED_MAX_SECTION_WEIGHT = 2000;
const FREESTANDING_MAX_SECTION_WEIGHT = 4000;

export const sectionParams = new Set<SectionParam>();

sectionParams.add({
	key: 'levelsCount',
	calculate: (section) =>
		`${section.selfs.length} ${getLevelsLabel(section.selfs.length, section.selfs.length)}`,
});

sectionParams.add({
	key: 'sectionHeight',
	calculate: (section, lng) => `${section.height} ${i18n.t('units.mm', { lng })}`,
});

sectionParams.add({
	key: 'sectionWidth',
	calculate: (section, lng) => `${section.width} ${i18n.t('units.mm', { lng })}`,
});

sectionParams.add({
	key: 'sectionDepth',
	calculate: (section, lng) => `${section.depth} ${i18n.t('units.mm', { lng })}`,
});

sectionParams.add({
	key: 'sectionMaterial',
	calculate: (_, lng) => {
		const {
			state: { material },
		} = useConfigurator.getState();
		const label = RACK_MATERIALS_OPTIONS.find((option) => option.value === material)?.label;

		if (!label) {
			return 'Unknown';
		}

		return i18n.t(label, { lng });
	},
});

sectionParams.add({
	key: 'shelfMaterial',
	calculate: (section, lng) => {
		const materials = Array.from(new Set(section.selfs.map((shelf) => shelf.deck)));
		const labels = materials.map(
			(material) => SHELF_DECK.find((option) => option.value === material)?.paramsLabel,
		);

		if (!labels) {
			return 'Unknown';
		}

		return labels.map((label) => i18n.t(label, { lng })).join(', ');
	},
});

sectionParams.add({
	key: 'maxLevelWeight',
	calculate: (section, lng) =>
		`${MAX_SHELF_WEIGHT[section.width]} ${i18n.t('units.kg', { lng })}`,
});

sectionParams.add({
	key: 'maxSectionWeight',
	calculate: (_, lng) => {
		const {
			state: { wallMounted },
		} = useConfigurator.getState();
		const value = wallMounted
			? WALL_MOUNTED_MAX_SECTION_WEIGHT
			: FREESTANDING_MAX_SECTION_WEIGHT;

		return `${value} ${i18n.t('units.kg', { lng })}`;
	},
});

sectionParams.add({
	key: 'levelsDistance',
	calculate: (section, lng) => {
		const availableHeight = section.height - SHELF_HEIGHT * section.selfs.length;
		const distance = Math.floor(availableHeight / (section.selfs.length + 1));
		return `${distance} ${i18n.t('units.mm', { lng })}`;
	},
});
